import React from 'react';
import { useParams } from 'react-router';

import { thirdPartyTransferDetailsURL } from '@admin/config/routes';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { useThirdPartyTransferDetailsQuery, useThirdPartyTransferUpdateMutation } from '@admin/schema';

import { formatUTCTimeStringForTimePickerInput } from '../fields/formik/formik_time_picker_form_group';
import { PageHeader } from '../helpers/page_header';
import { Spinner } from '../spinner';
import { Breadcrumbs } from './breadcrumbs';
import { FormValues, ThirdPartyTransferForm } from './form';

export const ThirdPartyTransferUpdate: React.FC = () => {
  useTitle('Edit Third-Party Transfer');
  const { accountID, thirdPartyTransferID } = useParams<{
    accountID: string;
    thirdPartyTransferID: string;
  }>();
  const { data: { thirdPartyTransfer } = {} } = useThirdPartyTransferDetailsQuery({
    client,
    variables: { id: thirdPartyTransferID },
  });

  const [execute, { loading: saving, data }] = useThirdPartyTransferUpdateMutation({
    client,
    onCompleted: ({ thirdPartyTransferUpdate }) => {
      if (thirdPartyTransferUpdate?.thirdPartyTransfer)
        window.location.href = thirdPartyTransferDetailsURL(accountID, thirdPartyTransferID);
    },
  });

  if (!accountID || !thirdPartyTransfer) return <Spinner />;

  const save = (input: FormValues) => {
    const { allItemsAcknowledged, ...filteredInput } = input;
    execute({ variables: { id: thirdPartyTransfer.id, input: { ...filteredInput, accountID } } });
  };

  const error = data?.thirdPartyTransferUpdate?.error;

  const defaults: FormValues = {
    originWarehouseID: thirdPartyTransfer.originWarehouse.id,
    destinationWarehouseID: thirdPartyTransfer.destinationWarehouse.id,
    pickupSLAStartDate: thirdPartyTransfer.pickupSLAStartDate,
    pickupSLAEndDate: thirdPartyTransfer.pickupSLAEndDate,
    pickupSLAStartTime: thirdPartyTransfer.pickupSLAStartTime
      ? formatUTCTimeStringForTimePickerInput(thirdPartyTransfer.pickupSLAStartTime)
      : '',
    pickupSLAEndTime: thirdPartyTransfer.pickupSLAEndTime
      ? formatUTCTimeStringForTimePickerInput(thirdPartyTransfer.pickupSLAEndTime)
      : '',
    deliverySLAStartDate: thirdPartyTransfer.deliverySLAStartDate,
    deliverySLAEndDate: thirdPartyTransfer.deliverySLAEndDate,
    deliverySLAStartTime: thirdPartyTransfer.deliverySLAStartTime
      ? formatUTCTimeStringForTimePickerInput(thirdPartyTransfer.deliverySLAStartTime)
      : '',
    deliverySLAEndTime: thirdPartyTransfer.deliverySLAEndTime
      ? formatUTCTimeStringForTimePickerInput(thirdPartyTransfer.deliverySLAEndTime)
      : '',
    bookingNumber: thirdPartyTransfer.bookingNumber,
    bookingPlatform: thirdPartyTransfer.bookingPlatform,
    cost: thirdPartyTransfer.cost,
    carrier: thirdPartyTransfer.carrier || '',
    allItemsAcknowledged: true,
  };

  return (
    <>
      <PageHeader>
        <Breadcrumbs thirdPartyTransfer={thirdPartyTransfer} editing />
      </PageHeader>
      <ThirdPartyTransferForm kind="update" save={save} saving={saving} error={error} defaults={defaults} />
    </>
  );
};
